import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import { isDevelopment } from '@/helpers/config'

export const supportedLanguages = ['en', 'es', 'fr', 'it', 'lt', 'lv', 'nl', 'no', 'pl', 'sv', 'da', 'nlBE', 'de'] as const
export type SupportedLanguage = (typeof supportedLanguages)[number]

export const defaultLanguage: SupportedLanguage = supportedLanguages[0]

/**
 * Normalizes language codes coming from browser / detector.
 * Keeps your existing project convention: "nlBE".
 */
const normalizeLanguage = (lng: string | undefined | null): SupportedLanguage => {
  const raw = (lng ?? '').trim()

  if (!raw) return defaultLanguage

  // common variants for Belgian Dutch coming from navigator / path
  if (raw === 'nl-BE' || raw === 'nl_BE' || raw.toLowerCase() === 'nl-be') return 'nlBE'

  // if you ever see "en-US" etc, you can map to base language:
  // const base = raw.split(/[-_]/)[0]
  // if (supportedLanguages.includes(base as SupportedLanguage)) return base as SupportedLanguage

  if (supportedLanguages.includes(raw as SupportedLanguage)) return raw as SupportedLanguage

  // try base language fallback for things like "de-DE"
  const base = raw.split(/[-_]/)[0]
  if (supportedLanguages.includes(base as SupportedLanguage)) return base as SupportedLanguage

  return defaultLanguage
}

const isLanguageSupported = (language: string): boolean => {
  return supportedLanguages.includes(normalizeLanguage(language))
}

/**
 * i18next backend that lazy-loads translations from ./languages/<lng>.json
 * (no more bundling all languages into main.js)
 */
const LazyJsonBackend = {
  type: 'backend' as const,
  read: async (lng: string, _, callback: (err: any, data: any) => void) => {
    try {
      const normalized = normalizeLanguage(lng)

      // We only have one namespace today ("translation"), but keep this shape for future.
      // Webpack will create one chunk per language JSON file.
      const mod = await import(
        /* webpackChunkName: "i18n-[request]" */
        `./languages/${normalized}.json`
      )

      const data = (mod as any).default ?? mod
      callback(null, data)
    } catch (e) {
      callback(e, false)
    }
  },
}

i18n
  .use(LanguageDetector)
  .use(LazyJsonBackend)
  .use(initReactI18next)
  .init({
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      // optional: if you use localStorage caching for chosen language
      // caches: ['localStorage'],
    },

    fallbackLng: defaultLanguage,
    supportedLngs: [...supportedLanguages], // keep your canonical list
    // keep existing behavior:
    keySeparator: false,
    returnNull: true,
    returnEmptyString: false,
    interpolation: { escapeValue: false },

    // IMPORTANT: we do NOT set `resources` anymore — backend loads them.
    ns: ['translation'],
    defaultNS: 'translation',

    react: {
      // avoids requiring <Suspense> around your app
      useSuspense: false,
    },

    debug: isDevelopment,
  })

/**
 * Loads a language resource bundle directly and adds it to the i18n store.
 * Bypasses i18n.loadLanguages() which has a race condition: it adds the lang
 * to the `preload` list before the load completes, so a concurrent second call
 * returns Promise.resolve() immediately — before resources are actually in the store.
 * Using import() directly is safe because webpack deduplicates concurrent requests
 * for the same chunk, and addResourceBundle() is synchronous and idempotent.
 */
export async function loadLanguageBundle(lang: SupportedLanguage): Promise<void> {
  if (i18n.hasResourceBundle(lang, 'translation')) return
  try {
    const mod = await import(
      /* webpackChunkName: "i18n-[request]" */
      `./languages/${lang}.json`
    )
    const data = (mod as any).default ?? mod
    i18n.addResourceBundle(lang, 'translation', data)
  } catch {
    // loading failed — getFixedT will fall back to the default language
  }
}

export { isLanguageSupported, normalizeLanguage }
export default i18n
